import { useOnceStore } from '@apps-once/store';
import { trackForInstallOnceStore } from '@apps-once/composables/track-for-install-once-store';
import { allAddOnDetails } from '@/apps/billing-shared/src/definitions';
import { getAnalyticsService } from '@apps-once/composables/get-analytics-service';
import { useSubscriptionStore } from '@apps-once/store/subscription';

export function trackTermsConditionsOpened({ eventSource, eventDetailsName }) {
  const analyticsService = getAnalyticsService();
  analyticsService.track(
    {
      name: 'VIEW_OPENED',
      screen: 'ONCE',
    },
    {
      source: eventSource,
      view: {
        details: {
          name: eventDetailsName,
          type: 'SCREEN',
        },
      },
    }
  );
}

export function trackLearnMoreOpened({
  eventSource,
  eventScreen,
  eventDetailsName,
  url,
}) {
  trackForInstallOnceStore(
    {
      name: 'VIEW_OPENED',
      screen: eventScreen,
    },
    {
      source: eventSource,
      view: {
        details: {
          name: eventDetailsName,
          url,
          type: 'URL',
        },
      },
    }
  );
}

export function trackAddOnSubscriptionEvent({
  eventName,
  eventSource,
  eventScreen,
  addOnId,
  errorMessage,
  isTrial,
  reasonName,
  alternateSolution,
  reasonDescription,
  addOns: { fromAddOns = [], toAddOns = [] } = {},
  viewDetails = {},
}) {
  const onceStore = useOnceStore();
  const isTrialValue = isTrial ?? false;
  const unsubscribe = Object.fromEntries(
    Object.entries({ reasonName, reasonDescription, alternateSolution }).filter(
      ([, v]) => v != null
    )
  );
  const details = {
    id: addOnId,
    name: allAddOnDetails[addOnId].eventDetailsName,
    isTrial: isTrialValue,
    ...(Object.keys(unsubscribe).length > 0 && { unsubscribe }),
  };

  if (errorMessage) {
    details.error = { message: errorMessage };
  }

  if (isTrialValue) {
    details.trialEndDate = onceStore.getAddOnTrialEndDateISOString(addOnId);
  }

  if (fromAddOns.length) {
    details.fromAddOns = fromAddOns;
  }

  if (toAddOns.length) {
    details.toAddOns = toAddOns;
  }

  trackForInstallOnceStore(
    {
      name: eventName,
      screen: eventScreen,
    },
    {
      source: eventSource,
      addOn: {
        details,
      },
      ...(Object.keys(viewDetails).length > 0 && { view: { ...viewDetails } }),
    }
  );
}

export function trackAddPromoCodeEvent({
  eventName,
  eventScreen,
  promoCode,
  errorMessage,
  eventSource,
  isAutoApplied,
}) {
  const details = {
    code: promoCode,
  };

  if (errorMessage) {
    details.error = { message: errorMessage };
  }

  // Callers that track add-on promotions pass an explicit boolean; validate
  // events omit it, so it stays absent there.
  if (isAutoApplied !== undefined) {
    details.isAutoApplied = isAutoApplied;
  }

  trackForInstallOnceStore(
    {
      name: eventName,
      screen: eventScreen,
    },
    {
      source: eventSource,
      promotion: {
        details,
      },
    }
  );
}

/**
 * Get mobile subscription tracking data if the current plan is a mobile plan.
 * Returns an object with { ID, application } or undefined if not a mobile plan.
 */
export function getMobileSubscriptionTracking() {
  const onceStore = useOnceStore();
  const mobileMetadata = onceStore.subscription?.mobileMetadata;

  if (!mobileMetadata) {
    return undefined;
  }

  const channelToApplication = {
    apple: 'IOS',
    google: 'ANDROID',
  };

  let mobileSubscriptionID = mobileMetadata.subscriptionID;

  if (
    mobileMetadata.channel?.toLowerCase() === 'apple' &&
    mobileMetadata.maxUserAllowed
  ) {
    // For Apple, normalize the subscription ID to "deputy_premium_mobile_<maxUserAllowed>_v03" for better tracking
    mobileSubscriptionID = `deputy_premium_mobile_${mobileMetadata.maxUserAllowed}_v03`;
  }

  return {
    ID: mobileSubscriptionID,
    application:
      channelToApplication[mobileMetadata.channel?.toLowerCase()] ||
      mobileMetadata.channel,
  };
}

/**
 * Track plan subscription events
 * @param {Object} params
 * @param {string} params.eventName - Event name ('INSTALLATION_EDITION_UPDATE_STARTED', 'INSTALLATION_EDITION_UPDATE_REQUESTED', 'INSTALLATION_EDITION_UPDATE_SUCCESS_ACKNOWLEDGED', 'INSTALLATION_EDITION_UPDATE_FAILURE_ACKNOWLEDGED')
 * @param {string} params.eventScreen - Screen name
 * @param {string} params.eventSource - Source ('Choose a plan modal' or 'Complete purchase')
 * @param {number} params.fromEditionId - From edition ID
 * @param {number} params.toEditionId - To edition ID
 * @param {Array} [params.fromAddOns=[]] - From add-ons array
 * @param {Array} [params.toAddOns=[]] - To add-ons array
 * @param {string|number} [params.installationId] - Installation ID (optional, only needed for some events)
 * @param {Object} [params.promotion] - Promotion object with code and description (optional)
 * @param {boolean} [params.hasAllInOneBundle=false] - Marks the update as an all-in-one bundle purchase (optional)
 */
export function trackInstallationEditionUpdateForInstallOnceStore({
  eventName,
  eventScreen,
  eventSource,
  fromEditionId,
  toEditionId,
  fromAddOns = [],
  toAddOns = [],
  installationId = '',
  promotion = undefined,
  hasAllInOneBundle = false,
}) {
  const subscriptionStore = useSubscriptionStore();
  const mobileSubscription = getMobileSubscriptionTracking();

  const fromEdition = {
    ID: fromEditionId,
    name: subscriptionStore.getPlanNameTracking(fromEditionId),
  };

  if (mobileSubscription) {
    fromEdition.mobileSubscription = mobileSubscription;
  }

  const eventData = {
    source: eventSource,
    installation: {
      details: {
        installationEditionUpdate: {
          fromEdition,
          toEdition: {
            ID: toEditionId,
            name: subscriptionStore.getPlanNameTracking(toEditionId),
          },
        },
      },
    },
  };

  // Mark all-in-one bundle purchases so analytics can filter them out of the
  // shared edition-update funnel.
  if (hasAllInOneBundle) {
    eventData.installation.details.installationEditionUpdate.hasAllInOneBundle = true;
  }

  // Add promotion details to toEdition if provided
  if (promotion) {
    eventData.installation.details.installationEditionUpdate.toEdition.promotion =
      {
        details: {
          code: promotion.code,
          description: promotion.description,
        },
      };
  }

  // Add add-ons and UUID only for certain events
  if (fromAddOns.length || toAddOns.length) {
    eventData.installation.details.installationEditionUpdate.fromAddOns =
      fromAddOns;
    eventData.installation.details.installationEditionUpdate.toAddOns =
      toAddOns;
  }

  if (installationId) {
    eventData.installation.details.UUID = installationId;
  }

  trackForInstallOnceStore(
    {
      name: eventName,
      screen: eventScreen,
    },
    eventData
  );
}
